"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import DataTable from "@/components/ui/DataTable";
import DrawerPanel from "@/components/ui/DrawerPanel";
import SearchableSelect from "@/components/ui/SearchableSelect";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import { Modal } from "@/components/ui/modal/index";
import { handleApiError } from "@/lib/api";
import { PencilIcon, TrashBinIcon } from "@/icons";
import { rmcClient } from "@/lib/rmcClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import toast from "react-hot-toast";

function emptyVehicleForm() {
  return {
    name: "",
    registration_no: "",
    driver_id: "",
    capacity_cum: "",
    date_of_purchase: new Date().toISOString().slice(0, 10),
    is_active: true,
    is_rented: false,
  };
}

function toPayload(form) {
  const payload = {
    name: form.name.trim(),
    registration_no: form.registration_no.trim(),
    capacity_cum: String(form.capacity_cum ?? "").trim() || "0",
    date_of_purchase: form.date_of_purchase,
    is_active: Boolean(form.is_active),
    is_rented: Boolean(form.is_rented),
  };
  if (!form.is_rented) {
    payload.driver_id = Number(form.driver_id);
  }
  return payload;
}

export default function RmcVehicles() {
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [form, setForm] = React.useState(emptyVehicleForm);
  const [vehicleToDelete, setVehicleToDelete] = React.useState(null);

  const { data: drivers = [] } = useQuery({
    queryKey: ["drivers"],
    queryFn: async () => (await rmcClient.listDrivers()).data,
  });
  const driverOptions = React.useMemo(
    () => drivers.filter((d) => d.is_active !== false),
    [drivers],
  );
  const { data: rows = [] } = useQuery({
    queryKey: ["vehicles"],
    queryFn: async () => (await rmcClient.listVehicles()).data,
  });

  const closeDrawer = () => {
    setOpen(false);
    setEditingId(null);
    setForm(emptyVehicleForm());
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyVehicleForm());
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name ?? row.alias ?? "",
      registration_no: row.registration_no ?? "",
      driver_id: row.driver_id != null ? String(row.driver_id) : "",
      capacity_cum: row.capacity_cum != null ? String(row.capacity_cum) : "",
      date_of_purchase: row.date_of_purchase
        ? String(row.date_of_purchase).slice(0, 10)
        : new Date().toISOString().slice(0, 10),
      is_active: row.is_active !== false,
      is_rented: row.is_rented === true,
    });
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: () => {
      if (!form.registration_no.trim() || (!form.is_rented && !form.driver_id)) {
        throw new Error("required");
      }
      const payload = toPayload(form);
      if (editingId != null) {
        return rmcClient.updateVehicle(editingId, payload);
      }
      return rmcClient.createVehicle(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vehicles"] });
      toast.success(editingId != null ? "Vehicle updated" : "Vehicle saved");
      closeDrawer();
    },
    onError: (e) => {
      if (e.message === "required") {
        toast.error(
          form.is_rented
            ? "Registration number is required."
            : "Registration number and driver are required.",
        );
      } else {
        toast.error(handleApiError(e));
      }
    },
  });

  const remove = useMutation({
    mutationFn: (id) => rmcClient.deleteVehicle(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vehicles"] });
      toast.success("Vehicle deleted");
      setVehicleToDelete(null);
    },
    onError: (e) => {
      setVehicleToDelete(null);
      toast.error(handleApiError(e));
    },
  });

  const dn = (id) => drivers.find((d) => d.id === id)?.name;

  return (
    <div className="space-y-6">
      <PageBreadCrumb pageTitle="Vehicle Master" />
      <div className="flex justify-end">
        <Button size="sm" onClick={openAdd}>
          Add Vehicle
        </Button>
      </div>
      <DataTable
        columns={[
          { key: "reg", header: "Registration", render: (r) => r.registration_no },
          {
            key: "name",
            header: "Name",
            render: (r) => r.name ?? r.alias ?? "—",
          },
          { key: "cap", header: "Capacity CUM", render: (r) => r.capacity_cum },
          {
            key: "rented",
            header: "Rented",
            render: (r) => (r.is_rented === true ? "Yes" : "No"),
          },
          {
            key: "driver",
            header: "Driver",
            render: (r) => (r.is_rented === true ? "—" : dn(r.driver_id) ?? "—"),
          },
          { key: "dop", header: "Purchase", render: (r) => r.date_of_purchase },
          {
            key: "active",
            header: "Active",
            render: (r) => (r.is_active === false ? "No" : "Yes"),
          },
          {
            key: "actions",
            header: "",
            headerClassName: "text-right",
            cellClassName: "text-right",
            render: (r) => (
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  aria-label={`Edit vehicle ${r.registration_no}`}
                  title="Edit"
                  disabled={save.isPending}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEdit(r);
                  }}
                >
                  <PencilIcon className="size-4" />
                </button>
                <button
                  type="button"
                  aria-label={`Delete vehicle ${r.registration_no}`}
                  title="Delete"
                  disabled={remove.isPending}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-red-50 hover:text-red-600 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-red-500/10 dark:hover:text-red-400 disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    setVehicleToDelete({
                      id: r.id,
                      label: r.registration_no,
                    });
                  }}
                >
                  <TrashBinIcon className="size-4" />
                </button>
              </div>
            ),
          },
        ]}
        data={rows}
        isLoading={false}
      />
      <DrawerPanel
        open={open}
        onClose={closeDrawer}
        title={editingId != null ? "Edit vehicle" : "Add vehicle"}
        widthClass="max-w-lg"
      >
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1"
              placeholder="e.g. Mixer 1"
            />
          </div>
          <div>
            <Label>Registration No</Label>
            <Input
              value={form.registration_no}
              onChange={(e) =>
                setForm({ ...form, registration_no: e.target.value.toUpperCase() })
              }
              onBlur={(e) =>
                setForm((f) => ({
                  ...f,
                  registration_no: e.target.value.toUpperCase(),
                }))
              }
              className="mt-1"
            />
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-300">
            <input
              type="checkbox"
              className="rounded border-gray-600"
              checked={form.is_rented}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  is_rented: e.target.checked,
                  driver_id: e.target.checked ? "" : f.driver_id,
                }))
              }
            />
            Rented vehicle (driver not required)
          </label>
          {!form.is_rented ? (
          <div>
            <SearchableSelect
              label="Driver"
              value={
                form.driver_id === "" || !Number.isFinite(Number(form.driver_id))
                  ? null
                  : Number(form.driver_id)
              }
              onChange={(v) =>
                setForm((f) => ({
                  ...f,
                  driver_id: v != null && v !== "" ? String(v) : "",
                }))
              }
              options={driverOptions}
              placeholder="Select driver…"
              overlayZClass="z-[100002]"
              menuZClass="z-[100003]"
            />
          </div>
          ) : null}
          <div>
            <Label>Capacity CUM</Label>
            <Input
              type="number"
              step="0.01"
              value={form.capacity_cum}
              onChange={(e) => setForm({ ...form, capacity_cum: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Date of purchase</Label>
            <Input
              type="date"
              value={form.date_of_purchase}
              onChange={(e) => setForm({ ...form, date_of_purchase: e.target.value })}
              className="mt-1"
            />
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-300">
            <input
              type="checkbox"
              className="rounded border-gray-600"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            Active
          </label>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              size="sm"
              disabled={
                !form.registration_no ||
                (!form.is_rented && !form.driver_id) ||
                save.isPending
              }
              onClick={() => save.mutate()}
            >
              {editingId != null ? "Update" : "Save"}
            </Button>
            <Button size="sm" variant="outline" onClick={closeDrawer}>
              Cancel
            </Button>
          </div>
        </div>
      </DrawerPanel>

      <Modal
        isOpen={!!vehicleToDelete}
        onClose={() => setVehicleToDelete(null)}
        className="max-w-md"
        showCloseButton={false}
      >
        <div className="space-y-4 p-1">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Delete vehicle <span className="font-semibold">{vehicleToDelete?.label}</span>?
          </p>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setVehicleToDelete(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={remove.isPending}
              onClick={() => vehicleToDelete && remove.mutate(vehicleToDelete.id)}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
